import paymentLinkApiService from '@/shared/services/payment-link-api.service'

import { checkNextPage } from '@/shared/lib/utils/pagination'
import i18n from '@/app/i18n'

import { TOAST_DISPATCHERS } from '@/shared/constants/store-constants/toast'
import { PAYMENT_LINK_DISPATCHERS } from '@/shared/constants'

function formatPaymentLinkToastMessage(payload, formData) {
  const deadline = new Date(
    payload.expiration?.deadline || formData.expiration?.deadline
  )
  const locale = i18n.global.locale.value === 'ru' ? 'ru-RU' : 'en-US'
  const deadlineFormatted = deadline.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  const coinId = (
    payload.data?.coin_id ||
    formData.data?.coin_id ||
    'USDT'
  ).toUpperCase()
  const isOneTime = payload.is_one_time || formData.is_one_time

  let amount
  if (
    payload.amount_boundaries?.max_amount ||
    formData.amount_boundaries?.max_amount
  ) {
    amount =
      payload.amount_boundaries?.max_amount ||
      formData.amount_boundaries?.max_amount
  } else {
    amount = payload.data?.amount || formData.data?.amount
  }

  if (isOneTime) {
    return i18n.global.t('toast|payment_link_created|one_time', {
      amount,
      coinId,
      deadline: deadlineFormatted,
    })
  }

  return i18n.global.t('toast|payment_link_created|regular', {
    amount,
    coinId,
    deadline: deadlineFormatted,
  })
}

export default {
  onSearch({ commit, dispatch }, payload) {
    commit('SET_SEARCH', payload)
    commit('INCREMENT_REQUEST_VERSION')
    commit('RESET_PAGE')
    dispatch(PAYMENT_LINK_DISPATCHERS.getPaymentLinks, {}, { root: true })
  },

  resetSearch({ commit, dispatch }) {
    commit('RESET_SEARCH')
    commit('INCREMENT_REQUEST_VERSION')
    commit('RESET_PAGE')
    dispatch(PAYMENT_LINK_DISPATCHERS.getPaymentLinks, {}, { root: true })
  },

  onDateRangeChange({ commit, dispatch }, payload) {
    commit('SET_DATE_RANGE', payload)
    commit('INCREMENT_REQUEST_VERSION')
    commit('RESET_PAGE')
    dispatch(PAYMENT_LINK_DISPATCHERS.getPaymentLinks, {}, { root: true })
  },

  resetDateRange({ commit, dispatch }) {
    commit('RESET_DATE_RANGE')
    commit('INCREMENT_REQUEST_VERSION')
    commit('RESET_PAGE')
    dispatch(PAYMENT_LINK_DISPATCHERS.getPaymentLinks, {}, { root: true })
  },

  onStatusesChange({ commit, dispatch }, payload) {
    commit('SET_STATUSES', payload)
    commit('INCREMENT_REQUEST_VERSION')
    commit('RESET_PAGE')
    dispatch(PAYMENT_LINK_DISPATCHERS.getPaymentLinks, {}, { root: true })
  },

  resetStatuses({ commit, dispatch }) {
    commit('RESET_STATUSES')
    commit('INCREMENT_REQUEST_VERSION')
    commit('RESET_PAGE')
    dispatch(PAYMENT_LINK_DISPATCHERS.getPaymentLinks, {}, { root: true })
  },

  resetFilters({ commit, dispatch }) {
    commit('RESET_SEARCH')
    commit('RESET_DATE_RANGE')
    commit('RESET_STATUSES')
    commit('INCREMENT_REQUEST_VERSION')
    dispatch(PAYMENT_LINK_DISPATCHERS.getPaymentLinks, {}, { root: true })
  },

  resetStore({ commit }) {
    commit('RESET_STORE')
  },

  resetPage({ commit }) {
    commit('RESET_PAGE')
  },

  changePage({ commit, dispatch, state }) {
    if (checkNextPage(state.page, state.total, state.limit)) {
      commit('SET_PAGE', state.page + 1)

      dispatch(PAYMENT_LINK_DISPATCHERS.getPaymentLinks, {}, { root: true })
    }
  },

  setId({ commit }, id) {
    commit('SET_ID', id)
  },

  parsePayload({ commit }, payload) {
    const { id, ...rest } = payload

    commit('SET_ID', id)

    Object.keys(rest).forEach((key) => {
      commit('SET_FORM_DATA', { [key]: payload[key] })
    })
  },

  onSelectSort({ commit, dispatch }, payload) {
    commit('RESET_PAGE')
    commit('SET_SORTING', payload)

    dispatch(PAYMENT_LINK_DISPATCHERS.getPaymentLinks, {}, { root: true })
  },

  onSelectChronologySort({ commit, dispatch, state }, payload) {
    commit('SET_CHRONOLOGY_SORT', payload)
    if (state.currentPaymentLink?.id) {
      dispatch('getPaymentLinkById', state.currentPaymentLink.id)
    }
  },

  async createPaymentLink({ commit, dispatch }, payload) {
    try {
      commit('SET_LOADING', true)

      const response = await paymentLinkApiService.createPaymentLink(payload)

      dispatch('parsePayload', { ...response, ...response.one_time })

      commit('RESET_PAGE')

      await dispatch(
        PAYMENT_LINK_DISPATCHERS.getPaymentLinks,
        {},
        { root: true }
      )

      const title = i18n.global.t('toast|payment_link_created|title')
      const message = formatPaymentLinkToastMessage(
        { ...response, ...response.one_time },
        payload
      )

      dispatch(
        TOAST_DISPATCHERS.setSuccessMessage,
        { title, message },
        { root: true }
      )

      return { success: true, payload: { ...response, ...response.one_time } }
    } catch (error) {
      if (!error.isHandled) {
        dispatch(TOAST_DISPATCHERS.setErrorMessage, {}, { root: true })
      }

      return { success: false }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  addPaymentLink({ commit }, payload) {
    commit('ADD_PAYMENT_LINK', payload)
  },

  async recreatePaymentLink({ commit, dispatch }, payload) {
    try {
      commit('SET_LOADING', true)

      const response = await paymentLinkApiService.createPaymentLink(payload)

      const payloadWithStats = {
        ...response,
        ...response.one_time,
        stats: {
          successful_invoices: 0,
          total_payment_amount: 0,
        },
      }

      dispatch('parsePayload', payloadWithStats)

      commit('SET_LOADING', false)

      dispatch(TOAST_DISPATCHERS.setSuccessMessage, {}, { root: true })

      return { success: true, payload: payloadWithStats }
    } catch (error) {
      if (!error.isHandled) {
        dispatch(TOAST_DISPATCHERS.setErrorMessage, {}, { root: true })
      }

      return { success: false }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  _addItemAfterUpdate({ commit }, payload) {
    commit('ADD_ITEM_AFTER_UPDATE', payload)
  },

  _changeItemAfterUpdate({ commit }, payload) {
    commit('CHANGE_ITEM_AFTER_UPDATE', payload)
  },

  async updatePaymentLink(
    { commit, dispatch, state },
    { showToast = true, ...formData }
  ) {
    try {
      commit('SET_ACTION_LOADING', true)

      const response = await paymentLinkApiService.updatePaymentLink(
        state.id,
        formData
      )

      dispatch('parsePayload', response)
      dispatch('_changeItemAfterUpdate', { ...response, ...response.one_time })

      if (showToast) {
        dispatch(TOAST_DISPATCHERS.setSuccessMessage, {}, { root: true })
      }

      return { success: true, payload: { ...response, ...response.one_time } }
    } catch (error) {
      if (!error.isHandled) {
        dispatch(TOAST_DISPATCHERS.setErrorMessage, {}, { root: true })
      }

      return { success: false }
    } finally {
      commit('SET_ACTION_LOADING', false)
    }
  },

  async updatePaymentLinkStatus({ commit, dispatch, state }, formData) {
    try {
      commit('SET_ACTION_LOADING', true)

      const response = await paymentLinkApiService.updatePaymentLinkStatus(
        state.id,
        formData
      )

      commit('SET_STATUS_AFTER_UPDATE', response)
      dispatch(TOAST_DISPATCHERS.setSuccessMessage, {}, { root: true })
    } catch (error) {
      if (!error.isHandled) {
        dispatch(TOAST_DISPATCHERS.setErrorMessage, {}, { root: true })
      }
    } finally {
      commit('SET_ACTION_LOADING', false)
    }
  },

  async deletePaymentLink({ commit, dispatch }, payload) {
    try {
      commit('SET_ACTION_LOADING', true)

      await paymentLinkApiService.deletePaymentLink(payload)

      dispatch(TOAST_DISPATCHERS.setSuccessMessage, {}, { root: true })

      commit('UPDATE_ITEMS_AFTER_DELETE', payload)
      commit('SET_ID', null)

      return { success: true }
    } catch (error) {
      if (!error.isHandled) {
        dispatch(TOAST_DISPATCHERS.setErrorMessage, {}, { root: true })
      }
      return { success: false }
    } finally {
      commit('SET_ACTION_LOADING', true)
    }
  },

  async getPaymentLinks({ commit, state, dispatch }) {
    const version = state._requestVersion

    try {
      commit('SET_LOADING', true)

      const params = {
        page: state.page,
        limit: state.limit,
        ...state.sort,
      }

      if (state.search) {
        params.search = state.search
      }

      if (
        state.dateRange &&
        Array.isArray(state.dateRange) &&
        state.dateRange.length === 2
      ) {
        const [dateFrom, dateTo] = state.dateRange

        if (dateFrom) {
          params.created_at_from = new Date(dateFrom).toISOString()
        }

        if (dateTo) {
          const endOfDay = new Date(dateTo)
          endOfDay.setHours(23, 59, 59, 999)
          params.created_at_to = endOfDay.toISOString()
        }
      }

      if (state.statuses && state.statuses.length > 0) {
        params.statuses = state.statuses.join(',')
      }

      const response =
        await paymentLinkApiService.getPaginatePaymentLink(params)

      commit('SET_ITEMS', { payload: response, version })
    } catch (error) {
      if (!error.isHandled) {
        dispatch(TOAST_DISPATCHERS.setErrorMessage, {}, { root: true })
      }
    } finally {
      if (version === state._requestVersion) {
        commit('SET_LOADING', false)
      }
    }
  },

  async getPaymentLinkById({ commit, dispatch, state }, id) {
    try {
      commit('SET_ACTION_LOADING', true)

      const response = await paymentLinkApiService.getPaymentLinkById(id, {
        chronology_sort_by: state.chronologySort.sort_by,
        chronology_sort_direction: state.chronologySort.sort_direction,
      })

      commit('SET_ID', id)
      commit('SET_CURRENT_PAYMENT_LINK', response)

      return { success: true, payload: response }
    } catch (error) {
      dispatch(TOAST_DISPATCHERS.setErrorMessage, {}, { root: true })
      return { success: false }
    } finally {
      commit('SET_ACTION_LOADING', false)
    }
  },

  async uploadAvatar({ dispatch }, { id, file }) {
    try {
      await paymentLinkApiService.uploadAvatar(id, file)

      await dispatch(PAYMENT_LINK_DISPATCHERS.getPaymentLinkById, id, {
        root: true,
      })

      dispatch(TOAST_DISPATCHERS.setSuccessMessage, {}, { root: true })

      return { success: true }
    } catch (error) {
      dispatch(TOAST_DISPATCHERS.setErrorMessage, {}, { root: true })
      return { success: false }
    }
  },

  async deleteAvatar({ dispatch }, id) {
    try {
      await paymentLinkApiService.deleteAvatar(id)

      await dispatch(PAYMENT_LINK_DISPATCHERS.getPaymentLinkById, id, {
        root: true,
      })

      dispatch(TOAST_DISPATCHERS.setSuccessMessage, {}, { root: true })

      return { success: true }
    } catch (error) {
      dispatch(TOAST_DISPATCHERS.setErrorMessage, {}, { root: true })
      return { success: false }
    }
  },

  resetPaymentLinkDetails({ commit }) {
    commit('RESET_PAYMENT_LINK_DETAILS')
  },

  async downloadExcelReport(
    { dispatch },
    { language, from, to, form_ids = [] }
  ) {
    try {
      const response = await paymentLinkApiService.getExcelReport({
        language,
        from,
        to,
        form_ids,
      })

      const url = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `EMCD-CryptoProcessingReport-${from}-${to}.xlsx`
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      dispatch(TOAST_DISPATCHERS.setSuccessMessage, {}, { root: true })

      return { success: true }
    } catch (error) {
      dispatch(TOAST_DISPATCHERS.setErrorMessage, {}, { root: true })
      return { success: false }
    }
  },
}
